import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Single axios instance so every request carries the token + base URL consistently.
const api = axios.create({ baseURL: API_BASE_URL });

api.interceptors.request.use((config) => {
  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('sessionId');
      localStorage.removeItem('client');
      sessionStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/* ---------------- Session helpers ---------------- */

export const getTeacherUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem('user'));
  } catch {
    return null;
  }
};

// NOTE: the sample login payload shared so far was for a *student* login
// (role: "student"), so the exact field holding a teacher's numeric ID
// (used as the `teacher=` / trainer id param in several endpoints below)
// isn't confirmed. This checks the most likely spots under employee_details
// and falls back to role_id. Once you can share a real teacher login
// response, point this at the correct field.
export const getTeacherId = () => {
  const user = getTeacherUser();
  return (
    user?.employee_details?.id ||
    user?.employee_details?.employee_id ||
    user?.employee_details?.teacher_id ||
    user?.role_id ||
    null
  );
};

export const getBranchId = () => getTeacherUser()?.branch_id || null;
export const getOrganizationId = () => getTeacherUser()?.organization_id || null;

/* ---------------- Assignments: create & manage ---------------- */

export const createAssignment = (formData) =>
  api.post(`/api/assignment/save/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

// params: { branch_id, organization_id } as needed
export const getAssignmentsList = (params = {}) =>
  api.get(`/api/assignment/list/`, { params });

export const getAssignmentDetail = (assignmentId) =>
  api.get(`/api/assignment/assignment/${assignmentId}/`);

export const getAssignmentPdfUrl = (filename) => `${API_BASE_URL}/media/assignments/${filename}`;

export const getAssignmentsByCourseBatch = (course, batch, teacher) =>
  api.get(`/api/assignment/course-batch/assignments/`, {
    params: {
      ...(course && { course }),
      ...(batch && { batch }),
      ...(teacher && { teacher }),
    },
  });

/* ---------------- Reviewing student submissions ---------------- */

export const getSubmissionsForAssignment = (assignmentId) =>
  api.get(`/api/assignment/assignment/submissions/`, { params: { assignment: assignmentId } });

export const getSubmissionDetail = (submissionId) =>
  api.get(`/api/assignment/assignment/submission/${submissionId}/`);

// payload: whatever your review form collects, e.g. { score, review }
export const reviewSubmission = (submissionId, payload) =>
  api.post(`/api/assignment/submission/review/${submissionId}/`, payload);

export const getStudentSubmissionStatusList = (assignmentId) =>
  api.get(`/api/assignment/assignment/submission/student-list/`, { params: { assignment: assignmentId } });

export const getStudentAssignmentsByCourseBatch = (admissionId, course, batch) =>
  api.get(`/api/assignment/student/assignments/course-batch/`, {
    params: {
      admission: admissionId,
      ...(course && { course }),
      ...(batch && { batch }),
    },
  });

/* ---------------- Teacher's own dashboard ---------------- */

export const getReviewDashboardCountAll = () =>
  api.get(`/api/assignment/assignment-review/dashboard-count/`);

// NOTE: the URL for "pending reviews for a specific trainer" was cut off in
// what was shared — this assumes the same endpoint filtered by a
// `teacher_id` param. Swap the param name (or the whole path, if it's a
// different endpoint entirely) once you confirm it.
export const getReviewDashboardCountForTeacher = (teacherId = getTeacherId()) =>
  api.get(`/api/assignment/assignment-review/dashboard-count/`, { params: { teacher_id: teacherId } });

export default api;
